import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList, Popover, PopoverContent, PopoverTrigger, SpecraButton } from '@mono/ui-components';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { ChevronsUpDown } from 'lucide-react';
import { useState } from 'react';
import { sharingApi } from '../../../../lib/api/sharingApi';
const DIRECTORY_KEY = 'delivery:platform-user-directory';
function userLabel(user) {
    if (!user) return '';
    return user.name?.trim() || user.email || '';
}
/**
 * Picks the platform user a registry contact IS. Search runs server-side so the
 * picker never holds the whole directory.
 *
 * DIRECTORY, NOT USER ADMINISTRATION: this reads the member-accessible sharing
 * directory (mariejois AuthService.ListUsersForSharing, tenant-confined
 * server-side from the caller's session — the same surface the share and
 * project-member pickers already use). It deliberately does NOT use
 * Admin/People's hooks: those call UserManagementService, which mariejois wraps
 * in NewAdminAuthInterceptor (main.go), so every ordinary delivery caller — the
 * VP and PM this form exists for — would get PermissionDenied and could not
 * link a contact at all. Registries is an ordinary delivery surface; only
 * /admin/people is legitimately admin-only.
 *
 * A STORED LINK IS LABELLED ONLY WHEN THE DIRECTORY ANSWERS FOR IT: there is no
 * member-accessible by-id user lookup, and inventing one out of the contact's
 * own email would happily put the wrong person's name against a stored id.
 * So an unlabelled link says "Linked platform user" rather than claiming the
 * link is broken or rendering a raw uuid — blank is reserved for "contact
 * only", which is a different, load-bearing state.
 */ export function PlatformUserPicker({ id, value, onChange, testid, triggerTestid, disabled, placeholder = 'Search people…', clearLabel }) {
    const [open, setOpen] = useState(false);
    const [search, setSearch] = useState('');
    // The pick made in this session labels itself; nothing else can, so losing it
    // on re-render would blank a label the user just saw.
    const [picked, setPicked] = useState(null);
    const results = useQuery({
        queryKey: [
            DIRECTORY_KEY,
            search
        ],
        placeholderData: keepPreviousData,
        queryFn: ()=>sharingApi.searchUsers(search)
    });
    const users = results.data ?? [];
    const directoryMatch = users.find((u)=>u.userId === value);
    const linkedLabel = picked?.userId === value ? userLabel(picked) : userLabel(directoryMatch);
    const triggerLabel = value ? linkedLabel || (results.isLoading ? 'Loading…' : 'Linked platform user') : placeholder;
    return /*#__PURE__*/ _jsxs(Popover, {
        open: open,
        onOpenChange: setOpen,
        children: [
            /*#__PURE__*/ _jsx(PopoverTrigger, {
                asChild: true,
                children: /*#__PURE__*/ _jsxs(SpecraButton, {
                    id: id,
                    "data-testid": triggerTestid,
                    disabled: disabled,
                    type: "button",
                    variant: "secondary",
                    role: "combobox",
                    "aria-expanded": open,
                    className: "h-10 w-full justify-between bg-background font-normal",
                    children: [
                        /*#__PURE__*/ _jsx("span", {
                            className: "truncate text-left",
                            children: triggerLabel
                        }),
                        /*#__PURE__*/ _jsx(ChevronsUpDown, {
                            className: "ml-2 h-4 w-4 shrink-0 opacity-50"
                        })
                    ]
                })
            }),
            /*#__PURE__*/ _jsx(PopoverContent, {
                className: "w-[var(--radix-popover-trigger-width)] p-0",
                align: "start",
                children: /*#__PURE__*/ _jsxs(Command, {
                    label: "Search people",
                    shouldFilter: false,
                    children: [
                        /*#__PURE__*/ _jsx(CommandInput, {
                            "data-testid": testid,
                            placeholder: "Search by name or email…",
                            value: search,
                            onValueChange: setSearch
                        }),
                        /*#__PURE__*/ _jsxs(CommandList, {
                            children: [
                                results.isLoading && /*#__PURE__*/ _jsx(CommandEmpty, {
                                    className: "py-4 text-center text-xs",
                                    children: "Searching…"
                                }),
                                !results.isLoading && users.length === 0 && /*#__PURE__*/ _jsx(CommandEmpty, {
                                    className: "py-4 text-center text-xs",
                                    children: "No platform users found"
                                }),
                                /*#__PURE__*/ _jsxs(CommandGroup, {
                                    children: [
                                        clearLabel && value && /*#__PURE__*/ _jsx(CommandItem, {
                                            value: "__clear__",
                                            onSelect: ()=>{
                                                setPicked(null);
                                                onChange('');
                                                setOpen(false);
                                                setSearch('');
                                            },
                                            className: "py-2 text-muted-foreground",
                                            children: clearLabel
                                        }),
                                        users.map((u)=>/*#__PURE__*/ _jsxs(CommandItem, {
                                                value: u.userId,
                                                "data-user-id": u.userId,
                                                onSelect: ()=>{
                                                    setPicked(u);
                                                    onChange(u.userId);
                                                    setOpen(false);
                                                    setSearch('');
                                                },
                                                className: "flex flex-col items-start gap-0.5 py-2",
                                                children: [
                                                    /*#__PURE__*/ _jsx("span", {
                                                        className: "font-medium",
                                                        children: userLabel(u)
                                                    }),
                                                    u.name && u.email && /*#__PURE__*/ _jsx("span", {
                                                        className: "text-[11px] text-muted-foreground",
                                                        children: u.email
                                                    })
                                                ]
                                            }, u.userId))
                                    ]
                                })
                            ]
                        })
                    ]
                })
            })
        ]
    });
}
